import {
  ComputedCell,
  DefaultHeatMapDatum,
  HeatMapDatum,
  HeatMapSerie,
  ResponsiveHeatMap,
} from '@nivo/heatmap'
import styled from '@emotion/styled'
import React from 'react'
import { useTheme } from '@ss/meta-core-components'
import { IDimension, IMargin } from '@types'
import { initTheme } from '@utils'

interface Props {
  data: HeatMapSerie<DefaultHeatMapDatum, Record<string, any>>[]
  dimensions?: IDimension
  dataCy?: string
  squaredBlock?: boolean
  customLegend?: any[]
  customAxisLeft?: any
  customAxisTop?: any
  customAxisBottom?: any
  customAxisRight?: any
  customMargin?: IMargin
  onClick?: (cell: ComputedCell<HeatMapDatum>, event: MouseEvent) => void
  customAnimate?: boolean
  customInteractive?: boolean
  customColors?: any
}

/**
 * Componente grafico di tipo heat map
 * @param props.data {Array<HeatMapSerie<DefaultHeatMapDatum, Record<string, never>>>} dati da visualizzare
 * @param props.dimensions {IDimension= } dimensioni del container
 * @param props.dataCy {string=} support test e2e
 * @param props.squaredBlock {boolean=} forza i blocchi ad essere quadrati
 * @param props.customLegend {Array<any>} permette di customizzare la legenda
 * @param props.customAxisTop {any} permette di customizzare l'asse in alto
 * @param props.customAxisBottom {any} permette di customizzare l'asse in basso
 * @param props.customMargin {IMargin= } permette di customizzare i margini
 * @param props.onClick {Function=} onClick sulla cella della heatmap
 * @component
 */
export const HeatMap: React.FC<Props> = (props) => {
  const {
    data,
    dataCy = '',
    dimensions = { height: '375px', width: '100%' },
    squaredBlock = false,
    customAxisTop,
    customAxisBottom,
    customAxisLeft,
    customAxisRight,
    customMargin,
    onClick,
  } = props
  const { type } = useTheme()

  const chartProps: any = {
    data: [...data],
    margin: customMargin || { top: 0, right: 0, bottom: 100, left: 0 },
    axisBottom: customAxisBottom,
    axisTop: customAxisTop,
    axisLeft: customAxisLeft,
    axisRight: customAxisRight,
    colors: props.customColors || {
      type: 'sequential',
      scheme: 'blues',
      minValue: undefined,
      maxValue: undefined,
    },
    forceSquare: squaredBlock,
    animate: props.customAnimate || false,
    isInteractive: props.customInteractive && true,
  }

  const legends: any[] = props.customLegend || [
    {
      anchor: 'bottom',
      translateX: 0,
      translateY: 25,
      length: 350,
      thickness: 8,
      direction: 'row',
      tickPosition: 'after',
      tickSize: 3,
      tickSpacing: 4,
      tickOverlap: false,
      titleAlign: 'start',
      titleOffset: 4,
    },
  ]

  return (
    <Container
      height={dimensions.height || '375px'}
      width={dimensions.width || '100%'}
      data-cy={`${dataCy}-heat-map-container`}
    >
      <ResponsiveHeatMap
        data-cy={`${dataCy}-heat-map`}
        {...chartProps}
        legends={legends}
        theme={{ ...initTheme(type), fontSize: 1 }}
        onClick={onClick}
        hoverTarget={'cell'}
      />
    </Container>
  )
}

const Container = styled.div<{ height: string; width: string }>`
  height: ${({ height }) => height};
  width: ${({ width }) => width};
`
